import { PropsWithChildren, useCallback, useMemo } from "react";
import {
  useLocation,
  useSearchParams,
  useNavigate,
  useParams,
  createSearchParams,
} from "react-router-dom";

import { useAppSelector } from "@redux/hooks/useAppSelector";
import {
  resetExternalCoverageData,
  resetPolicyTypeForm,
  selectCoverageType,
  selectHasDocumentsUploadFailed,
  selectIsLoading,
} from "@redux/coverages/coveragesSlice";
import { resetEventSlice } from "@redux/event/eventSlice";
import { RESIDENTS_ROUTES } from "@routing/Residents";
import { CoverageType } from "@enums";
import { COVERAGE_DATA_ROUTES } from "@routing/Residents/ProofOfCoverage/CoverageData";
import {
  ADD_EVENT_ROUTES,
  PROOF_OF_COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";
import { useResetFlowOnMissingData } from "./useResetFlowOnMissingData";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { DrawerFormLayout } from "@layouts/DrawerFormLayout/DrawerFormLayout";

const getDrawerNavigationItems = (
  coverageType: CoverageType,
  isAddingEvent: boolean
) =>
  isAddingEvent
    ? [
        {
          name: "Policy Event Type",
          path: ADD_EVENT_ROUTES.EVENT_TYPE,
        },
        {
          name: "Policy Event Details",
          path: ADD_EVENT_ROUTES.EVENT_DETAILS,
        },
        {
          name: "Documents",
          path: ADD_EVENT_ROUTES.EVENT_DOCUMENTS,
        },
        {
          name: "Policy Event Summary",
          path: ADD_EVENT_ROUTES.EVENT_SUMMARY,
        },
      ]
    : [
        {
          name: "Policy Type",
          path: PROOF_OF_COVERAGE_ROUTES.POLICY_TYPE,
        },
        ...(coverageType === CoverageType.External
          ? [
              {
                name: "Policy Document",
                path: PROOF_OF_COVERAGE_ROUTES.POLICY_DOCUMENT,
              },
              {
                name: "Policy Details",
                path: COVERAGE_DATA_ROUTES.EXTERNAL_COVERAGE,
              },
              {
                name: "Documents",
                path: PROOF_OF_COVERAGE_ROUTES.COVERAGE_DOCUMENTS,
              },
            ]
          : []),
        {
          name: "Policy Summary",
          path: COVERAGE_DATA_ROUTES.COVERAGE_DETAILS,
        },
      ];

interface ProofOfCoverageLayoutProps extends PropsWithChildren {
  onClosePath: string;
  cannotGoBackPaths: string[];
  isAddingEvent?: boolean;
}

export function ProofOfCoverageLayout({
  children,
  onClosePath,
  cannotGoBackPaths,
  isAddingEvent = false,
}: ProofOfCoverageLayoutProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { residentId: id } = useParams();
  const [searchParams] = useSearchParams();

  const isLoading = useAppSelector(selectIsLoading);
  const isUploadFailed = useAppSelector(selectHasDocumentsUploadFailed);
  const coverageType = useAppSelector(selectCoverageType);

  const drawerNavigationItems = useMemo(
    () => getDrawerNavigationItems(coverageType, isAddingEvent),
    [coverageType, isAddingEvent]
  );

  const onCloseDetailedPath = useMemo(
    () =>
      id
        ? `${onClosePath}/${RESIDENTS_ROUTES.RESIDENT}/${encodeURIComponent(id)}`
        : onClosePath,
    [onClosePath, id]
  );

  const onClose = useCallback(() => {
    dispatch(resetExternalCoverageData());
    dispatch(resetPolicyTypeForm());
    if (isAddingEvent) {
      dispatch(resetEventSlice());
    }
    navigate({
      pathname: onCloseDetailedPath,
      search: createSearchParams(searchParams).toString(),
    });
  }, [navigate, onCloseDetailedPath, isAddingEvent]);

  const canGoBack = useMemo(
    () => !cannotGoBackPaths.filter((step) => pathname.includes(step)).length,
    [pathname, cannotGoBackPaths]
  );

  useResetFlowOnMissingData(onCloseDetailedPath);

  return (
    <DrawerFormLayout
      canGoBack={canGoBack}
      drawerNavigationItems={drawerNavigationItems}
      isBackButtonDisabled={isLoading || isUploadFailed}
      onClose={onClose}
      title={
        isAddingEvent ? "Add New Policy Event" : "Add New Proof of Coverage"
      }>
      {children}
    </DrawerFormLayout>
  );
}

export default ProofOfCoverageLayout;
